import type {ComponentType} from 'react';

import ReactDOMServer from 'react-dom/server';

/**
 * The `iconAsBackground` function is designed to convert a React component representing an icon into a data URI string that can be used as a background image in CSS.
 * It takes a `ComponentType` representing the icon and an optional props object to be spread into the icon component.
 * This function is crucial for dynamically generating background images in styles, allowing for greater flexibility and customization in styling components.
 *
 * @param Icon  A `ComponentType` representing the React component of the icon to be converted.
 * @param props An optional object representing the props to be spread into the icon component.
 * @returns      A string representing the data URI of the rendered icon, which can be used as a background image in CSS.
 *
 * @example
 * ```tsx
 * const backgroundImage = iconAsBackground(MyIcon, { color: 'red' });
 * ```
 */
export const iconAsBackground = (Icon: ComponentType, props: object): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    // eslint-disable-next-line react/jsx-props-no-spreading
    ReactDOMServer.renderToStaticMarkup(<Icon {...props} />)
)}`;

/**
 * Creates a promise that resolves after a specified delay in milliseconds.
 * This utility function is useful for introducing artificial delays in asynchronous code, such as during testing,
 * animations, or throttling behavior. It wraps `setTimeout` in a promise to allow `await`-based syntax in async
 * functions.
 *
 * @param ms The number of milliseconds to wait before resolving the promise.
 * @returns A promise that resolves after the specified delay.
 *
 * @example
 * ```tsx
 * await wait(1000); // Waits for 1 second before continuing execution
 * ```
 */
export const wait = async (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Splits text by new lines and adds <br /> elements between lines.
 *
 * @param text The text to split.
 * @returns An array of strings and <br /> elements, or the original text if no new lines are present.
 */
export const splitText = (text?: string | null) => {
    if (!text) return text;

    const splitted = text.split('\n');

    if (splitted.length > 1) {
        // eslint-disable-next-line react/no-array-index-key
        return splitted.flatMap((part, index) => (index < splitted.length - 1 ? [part, <br key={index} />] : [part]));
    }

    return text;
};